import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { models } from "powerbi-client";
import { User } from "src/app/session/models/user";
import { UserService } from "src/app/session/services/user.service";
import { PresetsService } from "src/app/settings/services/presets.service";

import {
  ACCOUNT_NAME_ALEXANDRIA,
  ACCOUNT_NAME_BRAZIL,
  ACCOUNT_NAME_COTONOU,
  ACCOUNT_NAME_DELHI,
  ACCOUNT_NAME_DUBAI,
  ACCOUNT_NAME_DURBAN,
  ACCOUNT_NAME_ISTANBUL,
  ACCOUNT_NAME_MUMBAI,
  ACCOUNT_NAME_NAIROBI,
  ACCOUNT_NAME_NANTES,
  ACCOUNT_NAME_NETHERLANDS,
  ACCOUNT_NAME_SINGAPORE,
  AGRI_PRESET_REPORT,
  BRAZIL_MAIZE_REPORT,
  BRAZIL_SOYBEAN_IN_GRAINS_REPORT,
  BRAZIL_SOYBEAN_MEAL_REPORT,
  BRAZIL_SUGAR_REPORT,
  COORDINATOR_ROLE,
  CUSTOMER_ROLE,
  GLOBAL_MANAGER_ROLE,
  INDIA_DEFAULT_REPORT,
  INSPECTION_ANALYTICS,
  MANAGER_ROLE,
  NETHERLAND_DAILY_REPORT,
  NETHERLAND_MONTHLY_REPORT,
  NETHERLAND_TIMELOG_REPORT,
  VOC_AGRI_REPORT,
  VOC_INSPECTIONS_REPORT,
} from "../constants/constants";
import { EmbeddedDashboardService } from "../services/embedded-dashboard.service";

export enum EmbeddedTabsName {
  nofaDailyReport = "Nofa CIS Daily Report",
  nofaMonthlyReport = "Nofa CIS Monthly Report",
  nofaTimelogReport = "Nofa CIS Timelog",

  brazilMaizeReport = "Milho",
  brazilSoybeanInGrainsReport = "Soja em grãos",
  brazilSoybeanMealReport = "Farelo de Soja",
  brazilSugarReport = "Açúcar",

  vocInspectionsReport = "VOC Inspections Reports",
  vocAgrIinspections = "Agri inspections",
  inspectionAnalytics = "Inspection Analytics",
}

@Component({
  selector: "im-embedded-dashboard",
  templateUrl: "./embedded-dashboard.component.html",
  styleUrls: ["./embedded-dashboard.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EmbeddedDashboardComponent implements OnInit {
  private reportValue: string;
  public user: User;
  public reportClass = "report-container";
  public embeddedTabs = [];

  public embedConfig = {
    type: "report",
    id: "",
    embedUrl: "",
    accessToken: "",
    tokenType: models.TokenType.Embed,
    settings: {
      panes: {
        filters: {
          expanded: false,
          visible: false,
        },
      },
      background: models.BackgroundType.Transparent,
    },
  };

  public constructor(
    private readonly embeddedDashboardService: EmbeddedDashboardService,
    private readonly userService: UserService,
    private readonly presetsService: PresetsService,
  ) {}

  public ngOnInit(): void {
    this.initializeAsync();
  }

  public async initializeAsync(): Promise<void> {
    this.user = await this.userService.getCurrentUser();
    await this.defineEmbbededTabs();
  }

  private async defineEmbbededTabs(): Promise<void> {
    const { roles, accountName } = this.user;
    const hasCoordinator = roles.includes(COORDINATOR_ROLE);
    const hasManager = roles.includes(MANAGER_ROLE);
    const hasGlobalManager = roles.includes(GLOBAL_MANAGER_ROLE);

    this.addRoleTabs(hasCoordinator, hasManager, hasGlobalManager);
    this.addAccountTabs(accountName);
    await this.handleVocUser(accountName, roles);
    await this.handleDelhiAccount(
      accountName,
      hasCoordinator,
      hasManager,
      hasGlobalManager,
    );
    this.setReportValue(hasCoordinator, hasManager, hasGlobalManager);
    await this.getReportData();
  }

  private async getReportData(): Promise<void> {
    const reportResultData = await this.embeddedDashboardService.getReportData(
      this.reportValue,
    );

    this.embedConfig.embedUrl = reportResultData.embedUrl;
    this.embedConfig.accessToken = reportResultData.token;
    this.embedConfig.id = reportResultData.id;
  }

  private isVocUser(): boolean {
    const vocAccounts = [
      ACCOUNT_NAME_DURBAN,
      ACCOUNT_NAME_DUBAI,
      ACCOUNT_NAME_NANTES,
      ACCOUNT_NAME_MUMBAI,
      ACCOUNT_NAME_COTONOU,
      ACCOUNT_NAME_ALEXANDRIA,
      ACCOUNT_NAME_ISTANBUL,
      ACCOUNT_NAME_DELHI,
      ACCOUNT_NAME_NAIROBI,
      ACCOUNT_NAME_SINGAPORE,
    ];
    return vocAccounts.includes(this.user.accountName);
  }

  private addRoleTabs(
    hasCoordinator: boolean,
    hasManager: boolean,
    hasGlobalManager: boolean,
  ) {
    if (hasCoordinator || hasManager || hasGlobalManager) {
      this.embeddedTabs.push({
        name: EmbeddedTabsName.inspectionAnalytics,
        icon: "assignment",
      });
    }
  }

  private addAccountTabs(accountName: string) {
    if (
      accountName === ACCOUNT_NAME_NETHERLANDS &&
      !this.user.roles.includes(GLOBAL_MANAGER_ROLE)
    ) {
      this.embeddedTabs.push(
        {
          name: EmbeddedTabsName.nofaDailyReport,
          icon: "bar_chart",
        },
        {
          name: EmbeddedTabsName.nofaMonthlyReport,
          icon: "bar_chart",
        },
        {
          name: EmbeddedTabsName.nofaTimelogReport,
          icon: "bar_chart",
        },
      );
      this.reportValue = NETHERLAND_DAILY_REPORT;
    }

    if (
      accountName === ACCOUNT_NAME_BRAZIL &&
      !this.user.roles.includes(GLOBAL_MANAGER_ROLE)
    ) {
      this.embeddedTabs.push(
        {
          name: EmbeddedTabsName.brazilMaizeReport,
          icon: "bar_chart",
        },
        {
          name: EmbeddedTabsName.brazilSoybeanInGrainsReport,
          icon: "bar_chart",
        },
        {
          name: EmbeddedTabsName.brazilSoybeanMealReport,
          icon: "bar_chart",
        },
        {
          name: EmbeddedTabsName.brazilSugarReport,
          icon: "bar_chart",
        },
      );
      this.reportValue = BRAZIL_MAIZE_REPORT;
    }
  }

  private async handleVocUser(accountName: string, roles: string[]) {
    if (this.isVocUser()) {
      if (accountName === ACCOUNT_NAME_DELHI && roles.includes(CUSTOMER_ROLE)) {
        this.reportValue = INDIA_DEFAULT_REPORT;
      } else {
        this.embeddedTabs.push({
          name: EmbeddedTabsName.vocInspectionsReport,
          icon: "assignment",
        });
        this.reportValue = VOC_INSPECTIONS_REPORT;
      }
    }
  }

  private async handleDelhiAccount(
    accountName: string,
    hasCoordinator: boolean,
    hasManager: boolean,
    hasGlobalManager: boolean,
  ) {
    if (accountName === ACCOUNT_NAME_DELHI) {
      let shouldAddAgriTab = false;
      if (hasCoordinator) {
        const presets = await this.presetsService.getPresetsDetails();
        const hasAgriPreset = presets.some((preset) =>
          preset.name.toLowerCase().includes(AGRI_PRESET_REPORT),
        );
        shouldAddAgriTab = hasAgriPreset || presets.length === 0;
      }
      if (hasManager || hasGlobalManager) {
        shouldAddAgriTab = true;
      }
      if (shouldAddAgriTab) {
        this.embeddedTabs.push({
          name: EmbeddedTabsName.vocAgrIinspections,
          icon: "assignment",
        });
      }
    }
  }

  private setReportValue(
    hasCoordinator: boolean,
    hasManager: boolean,
    hasGlobalManager: boolean,
  ) {
    if (hasCoordinator || hasManager || hasGlobalManager) {
      this.reportValue = INSPECTION_ANALYTICS;
    }
  }

  public async onTabChanged(event: MatTabChangeEvent): Promise<void> {
    if (event) {
      switch (this.embeddedTabs[event.index].name) {
        case EmbeddedTabsName.nofaDailyReport:
          this.reportValue = NETHERLAND_DAILY_REPORT;
          break;
        case EmbeddedTabsName.nofaMonthlyReport:
          this.reportValue = NETHERLAND_MONTHLY_REPORT;
          break;
        case EmbeddedTabsName.nofaTimelogReport:
          this.reportValue = NETHERLAND_TIMELOG_REPORT;
          break;
        case EmbeddedTabsName.brazilMaizeReport:
          this.reportValue = BRAZIL_MAIZE_REPORT;
          break;
        case EmbeddedTabsName.brazilSoybeanInGrainsReport:
          this.reportValue = BRAZIL_SOYBEAN_IN_GRAINS_REPORT;
          break;
        case EmbeddedTabsName.brazilSoybeanMealReport:
          this.reportValue = BRAZIL_SOYBEAN_MEAL_REPORT;
          break;
        case EmbeddedTabsName.brazilSugarReport:
          this.reportValue = BRAZIL_SUGAR_REPORT;
          break;
        case EmbeddedTabsName.vocInspectionsReport:
          this.reportValue = VOC_INSPECTIONS_REPORT;
          break;
        case EmbeddedTabsName.vocAgrIinspections:
          this.reportValue = VOC_AGRI_REPORT;
          break;
        case EmbeddedTabsName.inspectionAnalytics:
          this.reportValue = INSPECTION_ANALYTICS;
          break;
        default:
          break;
      }
      this.embedConfig.accessToken = "";
      await this.getReportData();
    }
  }
}
